// ProfileSettings.js with Referral Tree and "Who Referred Me" section - Fixed Version

import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Avatar,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Badge,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  ArrowBack,
  Person,
  Email,
  Phone,
  AccountBalance,
  Save,
  Edit,
  Link as LinkIcon,
  TrendingUp,
  People,
  CheckCircle,
  Wallet,
  CardGiftcard,
  AccountTree,
  ExpandMore,
  ExpandLess,
  CalendarToday,
  Close,
  Warning,
  SupervisorAccount,
  PersonAdd,
  AdminPanelSettings,
  GroupWork,
  Star,
  Assessment,
  Refresh,
  VerifiedUser,
} from "@mui/icons-material";
import {
  doc,
  updateDoc,
  addDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from "firebase/firestore";

import { calculateAvailableDiscounts } from "./SignupPage";
import { db } from "../firebase/config";
import AddressManagement from "./AddressManagement";
import {
  fetchReferralTreeOptimized,
  calculateReferralCountsFromTree,
  clearReferralCache,
} from "../services/ReferralTreeService";
import { ADMIN_CONFIG } from "../config/appConfig";
import { brandPalette as primaryBlue } from "../config/brandTheme";
import {
  ULTRA_PLUS_FIELDS,
  getUltraPlusFieldValues,
  hasAllUltraPlusFields,
  getUltraPlusFieldLabels,
  ULTRA_PLUS_UNIQUE_FIELD_KEYS,
} from "../config/ultraPlusFields";
import { validateUltraPlusUniqueFields } from "../services/UltraPlusUniquenessService";

const ProfileSettings = ({
  user,
  userProfile,
  onBack,
  setShowPackageStore,
}) => {
  // State variables
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  
  // Admin check using centralized config
  const isAdmin = ADMIN_CONFIG.isAdmin(user?.email);
  const isUltraCustomer = userProfile?.hasPurchasedPackage === true;
  const canAccessUltraPlusFields = isUltraCustomer;
  
  // Edit restrictions for normal users - allow editing for first time
  const [hasEditedBefore, setHasEditedBefore] = useState(false);
  const [ultraPlusStatus, setUltraPlusStatus] = useState("none");
  const [ultraPlusRejectionReason, setUltraPlusRejectionReason] = useState("");
  const [ultraPlusRejectedFields, setUltraPlusRejectedFields] = useState([]);
  const [ultraPlusApprovedFields, setUltraPlusApprovedFields] = useState([]);
  const [submittingApproval, setSubmittingApproval] = useState(false);
  const canEdit = isAdmin || ultraPlusStatus !== "approved";
  const isUltraPlusFieldLocked = (fieldKey) => {
    if (ultraPlusStatus === "pending" || ultraPlusStatus === "approved") return true;
    if (ultraPlusStatus === "partially_rejected") {
      return ultraPlusApprovedFields.includes(fieldKey);
    }
    return false;
  };
  const isUltraPlusFieldRejected = (fieldKey) =>
    (ultraPlusStatus === "rejected" || ultraPlusStatus === "partially_rejected") &&
    ultraPlusRejectedFields.includes(fieldKey);
  const getUltraPlusFieldHelperText = (fieldKey, defaultText) => {
    if (isUltraPlusFieldRejected(fieldKey)) {
      return "Rejected by admin — please update and resubmit";
    }
    if (isUltraPlusFieldLocked(fieldKey)) {
      if (ultraPlusStatus === "pending") return "Pending admin approval";
      if (ultraPlusStatus === "partially_rejected") return "Approved — saved to your profile";
      return "Approved — contact admin to change";
    }
    return defaultText;
  };

  const [referralStats, setReferralStats] = useState(null);
  const [referralLoading, setReferralLoading] = useState(true);

  // Referral tree state
  const [treeDialogOpen, setTreeDialogOpen] = useState(false);
  const [referralTree, setReferralTree] = useState([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  // Referral count state
  const [countDialogOpen, setCountDialogOpen] = useState(false);
  const [referralCounts, setReferralCounts] = useState({});
  const [countLoading, setCountLoading] = useState(false);
  const [showUltraOnly, setShowUltraOnly] = useState(false);

  // Who referred me state
  const [myReferrer, setMyReferrer] = useState(null);
  const [referrerLoading, setReferrerLoading] = useState(true);

  // Form data
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    metaWhaleReferralLink: "",
    metaWhaleReceivingAddress: "",
    metaWhaleUplinerLink: "",
    metaWhaleUplinerReceivedAddress: "",
    vBoostReferralLink: "",
    vBoostReceivingAddress: "",
    vBoostUplinerLink: "",
    vBoostUplinerReceivedAddress: "",
  });

  const [uniqueFieldConflicts, setUniqueFieldConflicts] = useState({});
  const [checkingUniqueFields, setCheckingUniqueFields] = useState(false);

  const isUltraPlusUniqueField = (fieldKey) =>
    ULTRA_PLUS_UNIQUE_FIELD_KEYS.includes(fieldKey);

  const hasUniqueFieldConflicts =
    Object.keys(uniqueFieldConflicts).length > 0;

  // Discount info
  const [discountInfo, setDiscountInfo] = useState({
    cashback: { balance: 0, availableDiscount: 0, percentage: 10 },
    commission: { balance: 0, availableDiscount: 0, adminFee: 0, netAmount: 0 },
  });

  // Styles
  const glassStyles = {
    background: "rgba(255, 255, 255, 0.25)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.37)",
  };


  const cardGlassStyles = {
    background: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(15px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "20px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.2)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 16px 48px rgba(11, 28, 72, 0.35)",
      background: "rgba(255, 255, 255, 0.25)",
    },
  };

  // Helper functions
  const getActualCommissionBalance = () => {
    const newSystemBalance = userProfile?.commissionBalance || 0;
    const oldSystemBalance = userProfile?.confirmedCommissions || 0;
    return newSystemBalance > 0 ? newSystemBalance : oldSystemBalance;
  };

  const getActualCommissionEarned = () => {
    const newSystemEarned = userProfile?.commissionEarned || 0;
    const oldSystemEarned = userProfile?.totalCommissions || 0;
    return newSystemEarned > 0 ? newSystemEarned : oldSystemEarned;
  };

  const getTotalAvailableDiscount = () => {
    const cashbackDiscount = discountInfo.cashback.availableDiscount;
    const commissionDiscount = discountInfo.commission.netAmount;
    return cashbackDiscount + commissionDiscount;
  };

  // Fetch who referred the current user
  const fetchMyReferrer = async () => {
    try {
      setReferrerLoading(true);
      console.log("Fetching who referred user:", user.uid);

      const whoReferredMeQuery = query(
        collection(db, "referrals"),
        where("referredUserId", "==", user.uid)
      );
      const whoReferredMeSnapshot = await getDocs(whoReferredMeQuery);

      if (!whoReferredMeSnapshot.empty) {
        const referralData = whoReferredMeSnapshot.docs[0].data();

        const referrerQuery = query(
          collection(db, "users"),
          where("uid", "==", referralData.referrerId)
        );
        const referrerSnapshot = await getDocs(referrerQuery);

        if (!referrerSnapshot.empty) {
          const referrerUserData = referrerSnapshot.docs[0].data();

          const referrerStatsQuery = query(
            collection(db, "referrals"),
            where("referrerId", "==", referralData.referrerId)
          );
          const referrerStatsSnapshot = await getDocs(referrerStatsQuery);

          const referrerWithStats = {
            ...referralData,
            referrerUserData,
            referrerTotalReferrals: referrerStatsSnapshot.docs.length,
            referralId: whoReferredMeSnapshot.docs[0].id,
          };

          setMyReferrer(referrerWithStats);
        } else {
          setMyReferrer(null);
        }
      } else {
        setMyReferrer(null);
      }
    } catch (error) {
      console.error("Error fetching my referrer:", error);
      setMyReferrer(null);
    } finally {
      setReferrerLoading(false);
    }
  };

  // OPTIMIZED: Handle viewing referral tree using batch fetching
  const handleViewReferralTree = async (forceRefresh = false) => {
    setTreeDialogOpen(true);
    setTreeLoading(true);
    
    try {
      // Use optimized service - fetches ALL data in just 2 queries
      const tree = await fetchReferralTreeOptimized(user.uid, forceRefresh);
      setReferralTree(tree);
    } catch (error) {
      console.error("Error fetching referral tree:", error);
      setReferralTree([]);
    } finally {
      setTreeLoading(false);
    }
  };

  // Handle refresh button click
  const handleRefreshTree = () => {
    clearReferralCache();
    handleViewReferralTree(true);
  };

  // OPTIMIZED: Handle viewing referral count
  const handleViewReferralCount = async () => {
    setCountDialogOpen(true);
    setCountLoading(true);
    
    try {
      // Use optimized service
      const tree = await fetchReferralTreeOptimized(user.uid);
      const counts = calculateReferralCountsFromTree(tree);
      setReferralCounts(counts);
    } catch (error) {
      console.error("Error calculating referral counts:", error);
      setReferralCounts({});
    } finally {
      setCountLoading(false);
    }
  };

  // Count total referrals recursively (kept for local use)
  const countTotalReferrals = (referrals) => {
    let count = referrals.length;
    for (const referral of referrals) {
      count += countTotalReferrals(referral.subReferrals || []);
    }
    return count;
  };

  // Toggle expanded state for tree nodes
  const toggleExpanded = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  // Fetch referral statistics
  const fetchReferralStats = async () => {
    try {
      setReferralLoading(true);

      const commissionsQuery = query(
        collection(db, "commissions"),
        where("referrerId", "==", user.uid),
        where("isMultiLevelCommission", "==", true),
        orderBy("createdAt", "desc"),
        limit(50)
      );
      const commissionsSnapshot = await getDocs(commissionsQuery);
      const commissions = commissionsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const totalEarnings = commissions.reduce(
        (sum, commission) => sum + (commission.commissionAmount || 0),
        0
      );

      const confirmedEarnings = commissions
        .filter((c) => c.status === "confirmed")
        .reduce(
          (sum, commission) => sum + (commission.commissionAmount || 0),
          0
        );

      const pendingEarnings = commissions
        .filter((c) => c.status === "pending")
        .reduce(
          (sum, commission) => sum + (commission.commissionAmount || 0),
          0
        );

      const levelBreakdown = commissions.reduce((breakdown, commission) => {
        const level = commission.commissionLevel;
        if (!breakdown[level]) {
          breakdown[level] = {
            level,
            count: 0,
            totalAmount: 0,
            commissionRate: commission.commissionPercentage,
          };
        }
        breakdown[level].count++;
        breakdown[level].totalAmount += commission.commissionAmount;
        return breakdown;
      }, {});

      const myReferralsQuery = query(
        collection(db, "referrals"),
        where("referrerId", "==", user.uid),
        orderBy("createdAt", "desc")
      );
      const myReferralsSnapshot = await getDocs(myReferralsQuery);
      const myReferrals = myReferralsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const isEligibleForCommissions =
        userProfile?.hasPurchasedPackage || false;

      const stats = {
        totalEarnings: totalEarnings,
        pendingEarnings: pendingEarnings,
        confirmedEarnings: confirmedEarnings,
        totalCommissions: commissions.length,
        confirmedCommissions: commissions.filter(
          (c) => c.status === "confirmed"
        ).length,
        pendingCommissions: commissions.filter((c) => c.status === "pending")
          .length,
        isEligibleForCommissions,
        commissionHistory: commissions,
        levelBreakdown,
        referralHistory: myReferrals,
        packagePurchaseRequired: !isEligibleForCommissions,
        actualCommissionBalance: getActualCommissionBalance(),
        actualCommissionEarned: getActualCommissionEarned(),
      };

      setReferralStats(stats);
    } catch (error) {
      console.error("Error fetching referral stats:", error);
      setReferralStats({
        totalEarnings: 0,
        pendingEarnings: 0,
        confirmedEarnings: 0,
        totalCommissions: 0,
        confirmedCommissions: 0,
        pendingCommissions: 0,
        isEligibleForCommissions: false,
        commissionHistory: [],
        levelBreakdown: {},
        referralHistory: [],
        packagePurchaseRequired: true,
        actualCommissionBalance: 0,
        actualCommissionEarned: 0,
      });
    } finally {
      setReferralLoading(false);
    }
  };

  // Refresh user balance
  const refreshUserBalance = async () => {
    try {
      setLoading(true);

      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", user.uid)
      );
      const userSnapshot = await getDocs(userQuery);

      if (!userSnapshot.empty) {
        const updatedUserData = userSnapshot.docs[0].data();
        const actualCommissionBalance =
          updatedUserData.commissionBalance > 0
            ? updatedUserData.commissionBalance
            : updatedUserData.confirmedCommissions || 0;

        const modifiedUserData = {
          ...updatedUserData,
          commissionBalance: actualCommissionBalance,
        };

        const discounts = calculateAvailableDiscounts(modifiedUserData);
        setDiscountInfo(discounts);
        setSuccess("Balance information refreshed!");
      }
    } catch (error) {
      setError("Failed to refresh balance information: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const runUltraPlusUniquenessCheck = async (values = formData) => {
    if (!canAccessUltraPlusFields) {
      setUniqueFieldConflicts({});
      return { valid: true, conflicts: {} };
    }

    const hasAnyUniqueValue = ULTRA_PLUS_UNIQUE_FIELD_KEYS.some(
      (key) => String(values[key] || "").trim().length > 0
    );
    if (!hasAnyUniqueValue) {
      setUniqueFieldConflicts({});
      return { valid: true, conflicts: {} };
    }

    setCheckingUniqueFields(true);
    try {
      const result = await validateUltraPlusUniqueFields(
        getUltraPlusFieldValues(values),
        user.uid
      );
      setUniqueFieldConflicts(result.conflicts || {});
      return result;
    } finally {
      setCheckingUniqueFields(false);
    }
  };

  // Form handlers
  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    if (isUltraPlusUniqueField(field)) {
      setUniqueFieldConflicts((prev) => {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");

      if (!formData.name.trim()) {
        setError("Name is required");
        return;
      }

      if (!formData.phoneNumber.trim()) {
        setError("Phone number is required");
        return;
      }

      const phoneRegex = /^\+?[\d\s-()]{10,}$/;
      if (!phoneRegex.test(formData.phoneNumber)) {
        setError("Please enter a valid phone number");
        return;
      }

      if (canAccessUltraPlusFields) {
        const uniqueCheck = await runUltraPlusUniquenessCheck();
        if (!uniqueCheck.valid) {
          setError(
            "One or more T&S / V-Boost links or receiving addresses are already in the system. Please use unique values."
          );
          return;
        }
      }

      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", user.uid)
      );
      const userSnapshot = await getDocs(userQuery);

      if (userSnapshot.empty) {
        setError("User profile not found");
        return;
      }

      const userDocRef = doc(db, "users", userSnapshot.docs[0].id);

      const updateData = {
        name: formData.name.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        bankName: formData.bankName.trim(),
        accountNumber: formData.accountNumber.trim(),
        ifscCode: formData.ifscCode.trim().toUpperCase(),
        updatedAt: new Date().toISOString(),
      };

      // Track edit history for normal users
      if (!isAdmin) {
        const currentEditCount = userProfile.editCount || 0;
        updateData.editCount = currentEditCount + 1;
        updateData.lastEditedAt = new Date().toISOString();
        
        // For debugging: log the edit count
        console.log("Updating edit count:", { 
          currentEditCount, 
          newEditCount: updateData.editCount,
          userId: user.uid 
        });
      }

      // T&S / V-Boost are applied to the profile only after admin approval (all users).
      await updateDoc(userDocRef, updateData);

      const shouldSubmitUltraPlusForApproval =
        canAccessUltraPlusFields &&
        hasAllUltraPlusFields(formData) &&
        (ultraPlusStatus === "none" ||
          ultraPlusStatus === "rejected" ||
          ultraPlusStatus === "partially_rejected");

      if (shouldSubmitUltraPlusForApproval) {
        setLoading(false);
        await handleSubmitUltraPlusApproval();
        return;
      }

      setSuccess(
        canAccessUltraPlusFields
          ? "Profile saved. Fill all T&S and V-Boost fields and save again to send for admin approval."
          : "Profile saved successfully!"
      );
      setIsEditing(false);
      
      // Update edit status for normal users
      if (!isAdmin) {
        setHasEditedBefore(true);
      }
    } catch (err) {
      console.error("Error updating profile:", err);
      setError("Failed to update profile: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitUltraPlusApproval = async () => {
    try {
      setSubmittingApproval(true);
      setError("");
      setSuccess("");

      if (!canAccessUltraPlusFields) {
        setError("T&S and V-Boost details are available for Ultra customers only.");
        return;
      }

      if (!hasAllUltraPlusFields(formData)) {
        setError("Please fill in all T&S and V-Boost fields before submitting for approval.");
        return;
      }

      const uniqueCheck = await runUltraPlusUniquenessCheck();
      if (!uniqueCheck.valid) {
        setError(
          "One or more T&S / V-Boost links or receiving addresses are already in the system. Please use unique values before submitting."
        );
        return;
      }

      const pendingQuery = query(
        collection(db, "ultraPlusApprovalRequests"),
        where("userId", "==", user.uid),
        where("status", "==", "pending")
      );
      const pendingSnapshot = await getDocs(pendingQuery);
      if (!pendingSnapshot.empty) {
        setError("You already have a pending Ultra Plus approval request.");
        return;
      }

      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", user.uid)
      );
      const userSnapshot = await getDocs(userQuery);
      if (userSnapshot.empty) {
        setError("User profile not found");
        return;
      }

      const userDocRef = doc(db, "users", userSnapshot.docs[0].id);
      const now = new Date().toISOString();
      const fieldValues = getUltraPlusFieldValues(formData);

      const requestRef = await addDoc(collection(db, "ultraPlusApprovalRequests"), {
        userId: user.uid,
        userEmail: user.email,
        userName: formData.name.trim() || userProfile?.name || user.email,
        ...fieldValues,
        status: "pending",
        createdAt: now,
        updatedAt: now,
      });

      await updateDoc(userDocRef, {
        ultraPlusStatus: "pending",
        ultraPlusApproved: false,
        updatedAt: now,
      });

      setUltraPlusStatus("pending");
      setUltraPlusRejectionReason("");
      setUltraPlusRejectedFields([]);
      setUltraPlusApprovedFields([]);
      setIsEditing(false);

      setSuccess(
        "Your Ultra Plus details have been submitted for admin approval. You will be notified once reviewed."
      );
      console.log("Ultra Plus approval request created:", requestRef.id);
    } catch (err) {
      console.error("Error submitting Ultra Plus approval:", err);
      setError("Failed to submit for approval: " + err.message);
    } finally {
      setSubmittingApproval(false);
    }
  };

  // Utility functions
  const generateReferralLink = () => {
    const baseUrl = window.location.origin;
    const referralId = userProfile?.myReferralId;
    return `${baseUrl}/signup?ref=${referralId}`;
  };

  const handleShareReferralLink = () => {
    const referralLink = generateReferralLink();
    const message = `🎉 Check out InfinityWhale – an online shopping platform! Refer friends, earn points, and enjoy discounts. Start now with my referral link: ${referralLink}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleCopyReferralLink = async () => {
    try {
      const referralLink = generateReferralLink();
      await navigator.clipboard.writeText(referralLink);
      setSuccess("Referral link copied to clipboard!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      setError("Failed to copy link. Please try again.");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not available";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getRegistrationStatus = () => {
    if (userProfile?.hasPurchasedPackage) {
      return "🏆 Ultra Customer";
    }

    if (!userProfile?.registrationFee) return "New User";

    if (userProfile.registrationFee.confirmedByAdmin) {
      return "Confirmed by Admin";
    } else if (userProfile.registrationFee.paid) {
      return "Paid - Awaiting Confirmation";
    } else {
      return "Unpaid";
    }
  };

  const getRegistrationStatusColor = () => {
    if (userProfile?.hasPurchasedPackage) {
      return "success";
    }

    if (!userProfile?.registrationFee) return "default";

    if (userProfile.registrationFee.confirmedByAdmin) {
      return "success";
    } else if (userProfile.registrationFee.paid) {
      return "warning";
    } else {
      return "error";
    }
  };

  // Component for referral tree node
  const ReferralTreeNode = ({ referral, level = 0, parentId = "" }) => {
    const nodeId = `${parentId}-${referral.id}`;
    const hasSubReferrals =
      referral.subReferrals && referral.subReferrals.length > 0;
    const isExpanded = expandedNodes.has(nodeId);
    const userData = referral.referredUserData;

    return (
      <Box key={nodeId} sx={{ ml: level * 3 }}>
        <Card
          sx={{
            ...cardGlassStyles,
            mb: 1,
            bgcolor:
              level === 0
                ? "rgba(0, 114, 206, 0.1)"
                : "rgba(255, 255, 255, 0.1)",
            border:
              level === 0
                ? `2px solid ${primaryBlue[300]}`
                : "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                <Avatar
                  src={userData?.photoURL}
                  sx={{
                    width: 40,
                    height: 40,
                    mr: 2,
                    border: `2px solid ${
                      level === 0 ? primaryBlue[500] : primaryBlue[300]
                    }`,
                  }}
                >
                  <Person />
                </Avatar>

                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: level === 0 ? primaryBlue[800] : primaryBlue[700],
                      mb: 0.5,
                    }}
                  >
                    {userData?.name || "Unknown User"}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    {userData?.hasPurchasedPackage ? (
                      <Chip
                        label="Package Buyer"
                        size="small"
                        color="success"
                        icon={<CheckCircle />}
                      />
                    ) : (
                      <Chip
                        label="New User"
                        size="small"
                        color="default"
                        icon={<Warning />}
                      />
                    )}

                    <Chip
                      label={`Level ${level + 1}`}
                      size="small"
                      sx={{
                        bgcolor:
                          level === 0
                            ? primaryBlue[100]
                            : `rgba(${level * 40}, ${
                                150 + level * 20
                              }, 255, 0.2)`,
                        color:
                          level === 0 ? primaryBlue[800] : primaryBlue[700],
                        fontWeight: 600,
                      }}
                    />
                  </Box>

                  <Box
                    sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 2 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: primaryBlue[600],
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <CalendarToday sx={{ fontSize: 12, mr: 0.5 }} />
                      Referred:{" "}
                      {new Date(referral.createdAt).toLocaleDateString()}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color: primaryBlue[600],
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <AccountBalance sx={{ fontSize: 12, mr: 0.5 }} />
                      Code: {referral.referralCode}
                    </Typography>

                    {hasSubReferrals && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#00BFFF",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <People sx={{ fontSize: 12, mr: 0.5 }} />
                        {referral.totalSubReferrals} sub-referrals
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              {hasSubReferrals && (
                <IconButton
                  onClick={() => toggleExpanded(nodeId)}
                  sx={{
                    ml: 1,
                    color: primaryBlue[600],
                    "&:hover": { bgcolor: "rgba(0, 114, 206, 0.1)" },
                  }}
                >
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Sub-referrals */}
        <Collapse in={isExpanded}>
          {hasSubReferrals &&
            referral.subReferrals.map((subReferral) => (
              <ReferralTreeNode
                key={`${nodeId}-${subReferral.referredUserId}`}
                referral={subReferral}
                level={level + 1}
                parentId={nodeId}
              />
            ))}
        </Collapse>
      </Box>
    );
  };

  // Render "Who Referred Me" section
  const renderMyReferrerSection = () => {
    return (
      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{ ...glassStyles, p: 3, bgcolor: "rgba(255, 255, 255, 0.8)" }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              display: "flex",
              alignItems: "center",
              color: primaryBlue[800],
              fontWeight: 700,
              mb: 3,
            }}
          >
            <SupervisorAccount sx={{ mr: 2, color: primaryBlue[600] }} />
            Who Referred Me
          </Typography>

          {referrerLoading ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress sx={{ color: primaryBlue[500] }} />
              <Typography
                variant="body2"
                sx={{ mt: 2, color: primaryBlue[600] }}
              >
                Loading referrer information...
              </Typography>
            </Box>
          ) : myReferrer ? (
            <Card
              sx={{
                ...cardGlassStyles,
                bgcolor: "rgba(76, 175, 80, 0.1)",
                border: `2px solid rgba(76, 175, 80, 0.3)`,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Avatar
                    src={myReferrer.referrerUserData?.photoURL}
                    sx={{
                      width: 60,
                      height: 60,
                      mr: 3,
                      border: "3px solid #00BFFF",
                    }}
                  >
                    <Person sx={{ fontSize: 30 }} />
                  </Avatar>

                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#2e7d32",
                        mb: 1,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Star sx={{ color: "#D4AF37", mr: 1 }} />
                      {myReferrer.referrerUserData?.name || "Your Referrer"}
                    </Typography>

                    {/* <Typography
                      variant="body2"
                      sx={{ color: "#00BFFF", fontWeight: 600, mb: 1 }}
                    >
                      📧 {myReferrer.referrerUserData?.email}
                    </Typography> */}

                    {/* <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}
                    >
                      {myReferrer.referrerUserData?.hasPurchasedPackage ? (
                        <Chip
                          label="Package Buyer ⭐"
                          size="small"
                          sx={{
                            bgcolor: "#00BFFF",
                            color: "white",
                            fontWeight: 600,
                          }}
                        />
                      ) : (
                        <Chip
                          label="New User"
                          size="small"
                          color="default"
                          icon={<Warning />}
                        />
                      )}

                      <Chip
                        label={`${myReferrer.referrerTotalReferrals} Total Referrals`}
                        size="small"
                        sx={{
                          bgcolor: primaryBlue[100],
                          color: primaryBlue[800],
                          fontWeight: 600,
                        }}
                        icon={<People />}
                      />
                    </Box> */}

                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#00BFFF",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <CalendarToday sx={{ fontSize: 12, mr: 0.5 }} />
                        You were referred: {formatDate(myReferrer.createdAt)}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          color: primaryBlue[600],
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <AccountBalance sx={{ fontSize: 12, mr: 0.5 }} />
                        Referral Code: {myReferrer.referralCode}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Alert
                  severity="info"
                  sx={{ bgcolor: "rgba(0, 114, 206, 0.1)" }}
                >
                  <Typography variant="body2">
                    💡 <strong>Thank your referrer!</strong> They introduced you
                    to this platform. When you make purchases, they may earn
                    referral points based on our affiliate system.
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          ) : (
            <Alert
              severity="info"
              sx={{
                ...cardGlassStyles,
                bgcolor: "rgba(0, 114, 206, 0.1)",
                border: `2px solid rgba(0, 114, 206, 0.3)`,
              }}
            >
              <Typography variant="body1">
                <strong>Direct Registration</strong>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                You joined our platform directly without using a referral link.
                You're a founding member! 🎉
              </Typography>
            </Alert>
          )}
        </Paper>
      </Grid>
    );
  };

  // Effects
  useEffect(() => {
    if (!isEditing || !canAccessUltraPlusFields) return undefined;

    const timeoutId = setTimeout(() => {
      runUltraPlusUniquenessCheck(formData);
    }, 700);

    return () => clearTimeout(timeoutId);
  }, [
    isEditing,
    canAccessUltraPlusFields,
    formData.metaWhaleReferralLink,
    formData.metaWhaleReceivingAddress,
    formData.vBoostReferralLink,
    formData.vBoostReceivingAddress,
  ]);

  useEffect(() => {
    if (userProfile) {
      setFormData({
        name: userProfile.name || "",
        phoneNumber: userProfile.phoneNumber || "",
        bankName: userProfile.bankName || "",
        accountNumber: userProfile.accountNumber || "",
        ifscCode: userProfile.ifscCode || "",
        metaWhaleReferralLink: userProfile.metaWhaleReferralLink || "",
        metaWhaleReceivingAddress: userProfile.metaWhaleReceivingAddress || "",
        metaWhaleUplinerLink: userProfile.metaWhaleUplinerLink || "",
        metaWhaleUplinerReceivedAddress:
          userProfile.metaWhaleUplinerReceivedAddress || "",
        vBoostReferralLink: userProfile.vBoostReferralLink || "",
        vBoostReceivingAddress: userProfile.vBoostReceivingAddress || "",
        vBoostUplinerLink: userProfile.vBoostUplinerLink || "",
        vBoostUplinerReceivedAddress:
          userProfile.vBoostUplinerReceivedAddress || "",
      });

      // Check if user has edited their profile before
      // For first-time users, editCount should be undefined/0 and lastEditedAt should be undefined
      // Only consider them as having edited if editCount is explicitly > 0
      const hasEdited = userProfile.editCount && userProfile.editCount > 0;
      
      // Debug logging to help troubleshoot edit restrictions
      console.log("Edit restriction check:", {
        userId: user.uid,
        userEmail: user.email,
        editCount: userProfile.editCount,
        lastEditedAt: userProfile.lastEditedAt,
        hasEdited,
        isAdmin,
        canEdit: isAdmin || !hasEdited
      });
      
      setHasEditedBefore(hasEdited);

      const status = userProfile.ultraPlusStatus || "none";
      setUltraPlusStatus(status);

      if (status === "rejected" || status === "partially_rejected") {
        setUltraPlusRejectedFields(userProfile.ultraPlusRejectedFields || []);
        setUltraPlusApprovedFields(userProfile.ultraPlusApprovedFields || []);

        const reviewQuery = query(
          collection(db, "ultraPlusApprovalRequests"),
          where("userId", "==", user.uid),
          where("status", "==", status),
          orderBy("createdAt", "desc"),
          limit(1)
        );
        getDocs(reviewQuery).then((snapshot) => {
          if (!snapshot.empty) {
            const data = snapshot.docs[0].data();
            setUltraPlusRejectionReason(data.rejectionReason || "");
            setUltraPlusRejectedFields(
              data.rejectedFields || userProfile.ultraPlusRejectedFields || []
            );
            setUltraPlusApprovedFields(
              data.approvedFields || userProfile.ultraPlusApprovedFields || []
            );
            setFormData((prev) => ({
              ...prev,
              ...getUltraPlusFieldValues({ ...userProfile, ...data }),
            }));
          } else {
            setFormData((prev) => ({
              ...prev,
              ...getUltraPlusFieldValues(userProfile),
            }));
          }
        });
      } else if (status === "pending") {
        const pendingQuery = query(
          collection(db, "ultraPlusApprovalRequests"),
          where("userId", "==", user.uid),
          where("status", "==", "pending"),
          orderBy("createdAt", "desc"),
          limit(1)
        );
        getDocs(pendingQuery).then((snapshot) => {
          if (!snapshot.empty) {
            setFormData((prev) => ({
              ...prev,
              ...getUltraPlusFieldValues(snapshot.docs[0].data()),
            }));
          }
        });
        setUltraPlusRejectionReason("");
        setUltraPlusRejectedFields([]);
        setUltraPlusApprovedFields([]);
      } else {
        setUltraPlusRejectionReason("");
        setUltraPlusRejectedFields([]);
        setUltraPlusApprovedFields([]);
      }

      const actualCommissionBalance = getActualCommissionBalance();
      const modifiedUserProfile = {
        ...userProfile,
        commissionBalance: actualCommissionBalance,
      };

      const discounts = calculateAvailableDiscounts(modifiedUserProfile);
      setDiscountInfo(discounts);
    }

    fetchReferralStats();
    fetchMyReferrer();
  }, [userProfile, user.uid]);

  // Main render
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, 
        #F0F8FF 0%, 
        #E6F0FA 25%, 
        #C5DDF5 50%, 
        #E6F0FA 75%, 
        #F0F8FF 100%)`,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
          radial-gradient(circle at 20% 50%, rgba(0, 114, 206, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(11, 28, 72, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 40% 80%, rgba(0, 34, 102, 0.1) 0%, transparent 50%)
        `,
          zIndex: 0,
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1 }}>
        {/* Navigation Bar */}
        <Box sx={{ ...glassStyles, m: 2, p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              onClick={onBack}
              sx={{
                mr: { xs: 1, sm: 2 },
                bgcolor: "#1E40AF",
                color: "white",
                "&:hover": { bgcolor: "#001A4D" },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography
              variant={{ xs: "h5", sm: "h4" }}
              sx={{
                fontWeight: 700,
                background: `linear-gradient(45deg, #001A4D, #1E40AF)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Profile Settings
            </Typography>
          </Box>
        </Box>

        <Container maxWidth="md" sx={{ pb: 4 }}>
          {/* Alerts */}
          {error && (
            <Alert severity="error" sx={{ mb: 2, ...glassStyles }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2, ...glassStyles }}>
              {success}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Profile Overview Card */}
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 3, textAlign: "center" }}>
                <Avatar
                  src={user.photoURL}
                  sx={{ width: 100, height: 100, mx: "auto", mb: 2 }}
                >
                  <Person sx={{ fontSize: 60 }} />
                </Avatar>
                <Typography variant="h6" gutterBottom>
                  {userProfile?.name || user.displayName || "User"}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {user.email}
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <Chip
                    label={getRegistrationStatus()}
                    color={getRegistrationStatusColor()}
                    size="small"
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    <strong>User ID:</strong>
                  </Typography>
                  <Chip
                    label={userProfile?.serialNumber ? `#${userProfile.serialNumber}` : "Loading..."}
                    color="secondary"
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    <strong>My Referral ID:</strong>
                  </Typography>
                  <Chip
                    label={userProfile?.myReferralId || "Loading..."}
                    color="primary"
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ mt: 1 }}
                  >
                    <strong>My Referral Link:</strong>
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: "grey.100",
                      p: 1,
                      borderRadius: 1,
                      mb: 1,
                      border: "1px solid",
                      borderColor: "grey.300",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: "monospace",
                        wordBreak: "break-all",
                        fontSize: "0.7rem",
                      }}
                    >
                      {generateReferralLink()}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<LinkIcon />}
                      onClick={handleShareReferralLink}
                      sx={{ fontSize: "0.7rem", py: 0.5 }}
                    >
                      WhatsApp
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleCopyReferralLink}
                      sx={{ fontSize: "0.7rem", py: 0.5 }}
                    >
                      Copy
                    </Button>
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    <strong>Total Referrals:</strong>{" "}
                    {userProfile?.totalReferrals || 0}
                  </Typography>

                  <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<AccountTree />}
                      onClick={handleViewReferralTree}
                      fullWidth
                      sx={{ fontSize: "0.8rem" }}
                    >
                      View Referral Tree
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<Assessment />}
                      onClick={handleViewReferralCount}
                      fullWidth
                      sx={{ fontSize: "0.8rem" }}
                    >
                      View Referral Count
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Profile Details Card */}
            <Grid item xs={12} md={8}>
              <Paper
                elevation={0}
                sx={{
                  ...glassStyles,
                  p: 3,
                  bgcolor: "rgba(255, 255, 255, 0.8)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Typography variant="h6">Profile Information</Typography>
                  {!isEditing ? (
                    canEdit ? (
                      <Button
                        variant="outlined"
                        startIcon={<Edit />}
                        onClick={() => setIsEditing(true)}
                      >
                        Edit Profile
                      </Button>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Button
                          variant="outlined"
                          startIcon={<AdminPanelSettings />}
                          disabled
                          sx={{ 
                            color: "text.secondary",
                            borderColor: "text.disabled",
                            '&.Mui-disabled': {
                              color: "text.secondary",
                              borderColor: "text.disabled",
                            }
                          }}
                        >
                          Editing Restricted
                        </Button>
                      </Box>
                    )
                  ) : (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setIsEditing(false);
                          setFormData({
                            name: userProfile?.name || "",
                            phoneNumber: userProfile?.phoneNumber || "",
                            bankName: userProfile?.bankName || "",
                            accountNumber: userProfile?.accountNumber || "",
                            ifscCode: userProfile?.ifscCode || "",
                            metaWhaleReferralLink:
                              userProfile?.metaWhaleReferralLink || "",
                            metaWhaleReceivingAddress:
                              userProfile?.metaWhaleReceivingAddress || "",
                            metaWhaleUplinerLink:
                              userProfile?.metaWhaleUplinerLink || "",
                            metaWhaleUplinerReceivedAddress:
                              userProfile?.metaWhaleUplinerReceivedAddress ||
                              "",
                            vBoostReferralLink:
                              userProfile?.vBoostReferralLink || "",
                            vBoostReceivingAddress:
                              userProfile?.vBoostReceivingAddress || "",
                            vBoostUplinerLink:
                              userProfile?.vBoostUplinerLink || "",
                            vBoostUplinerReceivedAddress:
                              userProfile?.vBoostUplinerReceivedAddress || "",
                          });
                          setError("");
                          setSuccess("");
                          setUniqueFieldConflicts({});
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<Save />}
                        onClick={handleSaveProfile}
                        disabled={
                          loading ||
                          checkingUniqueFields ||
                          hasUniqueFieldConflicts
                        }
                      >
                        {loading ? <CircularProgress size={20} /> : "Save"}
                      </Button>
                    </Box>
                  )}
                </Box>

                {!isAdmin && !canAccessUltraPlusFields && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    T&S and V-Boost details are available for Ultra customers only. Purchase a
                    package to become an Ultra customer and unlock these fields.
                  </Alert>
                )}

                {canAccessUltraPlusFields && ultraPlusStatus === "pending" && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Your T&S and V-Boost details are pending admin approval.
                  </Alert>
                )}
                {canAccessUltraPlusFields && ultraPlusStatus === "approved" && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Your Ultra Plus details have been approved and saved to your profile.
                  </Alert>
                )}
                {canAccessUltraPlusFields && ultraPlusStatus === "partially_rejected" && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Some fields were approved and saved to your profile. Please update the
                    rejected fields below and submit again.
                    {ultraPlusRejectedFields.length > 0 && (
                      <>
                        {" "}
                        Rejected: {getUltraPlusFieldLabels(ultraPlusRejectedFields).join(", ")}.
                      </>
                    )}
                    {ultraPlusRejectionReason ? ` Note: ${ultraPlusRejectionReason}` : ""}
                  </Alert>
                )}
                {canAccessUltraPlusFields && ultraPlusStatus === "rejected" && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Your Ultra Plus submission was rejected
                    {ultraPlusRejectedFields.length > 0 && (
                      <>
                        {" "}
                        for: {getUltraPlusFieldLabels(ultraPlusRejectedFields).join(", ")}.
                      </>
                    )}
                    {ultraPlusRejectionReason
                      ? ` ${ultraPlusRejectionReason}`
                      : " Please update the details and submit again for approval."}
                  </Alert>
                )}

                {/* Edit Restriction Notice */}
                {canAccessUltraPlusFields && hasEditedBefore && ultraPlusStatus !== "approved" && (
                  <Alert 
                    severity="info" 
                    sx={{ mb: 2 }}
                    icon={<AdminPanelSettings />}
                  >
                    T&S and V-Boost details must be submitted for admin approval. They will be saved to your profile only after approval.
                  </Alert>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      value={formData.name}
                      onChange={handleInputChange("name")}
                      disabled={!isEditing}
                      InputProps={{
                        startAdornment: (
                          <Person sx={{ mr: 1, color: "text.secondary" }} />
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      value={user.email}
                      disabled
                      helperText="Email cannot be changed as it's linked to your Google account"
                      InputProps={{
                        startAdornment: (
                          <Email sx={{ mr: 1, color: "text.secondary" }} />
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={formData.phoneNumber}
                      onChange={handleInputChange("phoneNumber")}
                      disabled={!isEditing}
                      placeholder="+1 (555) 123-4567"
                      InputProps={{
                        startAdornment: (
                          <Phone sx={{ mr: 1, color: "text.secondary" }} />
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Bank Name"
                      value={formData.bankName}
                      onChange={handleInputChange("bankName")}
                      disabled={!isEditing}
                      placeholder="Enter your bank name"
                      InputProps={{
                        startAdornment: (
                          <AccountBalance sx={{ mr: 1, color: "text.secondary" }} />
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Account Number"
                      value={formData.accountNumber}
                      onChange={handleInputChange("accountNumber")}
                      disabled={!isEditing}
                      placeholder="Enter account number"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="IFSC Code"
                      value={formData.ifscCode}
                      onChange={handleInputChange("ifscCode")}
                      disabled={!isEditing}
                      placeholder="Enter IFSC code"
                    />
                  </Grid>

                  {canAccessUltraPlusFields && (
                  <>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="T&S Referral Link"
                      value={formData.metaWhaleReferralLink}
                      onChange={handleInputChange("metaWhaleReferralLink")}
                      disabled={!isEditing || isUltraPlusFieldLocked("metaWhaleReferralLink")}
                      placeholder="Enter your T&S referral link"
                      error={
                        isUltraPlusFieldRejected("metaWhaleReferralLink") ||
                        Boolean(uniqueFieldConflicts.metaWhaleReferralLink)
                      }
                      helperText={
                        uniqueFieldConflicts.metaWhaleReferralLink ||
                        getUltraPlusFieldHelperText(
                          "metaWhaleReferralLink",
                          "Your T&S referral link for sharing"
                        )
                      }
                      InputProps={{
                        startAdornment: (
                          <LinkIcon sx={{ mr: 1, color: "text.secondary" }} />
                        ),
                        endAdornment: isUltraPlusFieldLocked("metaWhaleReferralLink") && (
                          <AdminPanelSettings 
                            sx={{ 
                              color: "text.secondary",
                              ml: 1 
                            }} 
                            fontSize="small"
                          />
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="T&S Receiving Address"
                      value={formData.metaWhaleReceivingAddress}
                      onChange={handleInputChange("metaWhaleReceivingAddress")}
                      disabled={!isEditing || isUltraPlusFieldLocked("metaWhaleReceivingAddress")}
                      placeholder="Enter your T&S receiving address"
                      error={
                        isUltraPlusFieldRejected("metaWhaleReceivingAddress") ||
                        Boolean(uniqueFieldConflicts.metaWhaleReceivingAddress)
                      }
                      helperText={
                        uniqueFieldConflicts.metaWhaleReceivingAddress ||
                        getUltraPlusFieldHelperText(
                          "metaWhaleReceivingAddress",
                          "Address where you receive T&S payments"
                        )
                      }
                      multiline
                      rows={2}
                      InputProps={{
                        startAdornment: (
                          <AccountBalance
                            sx={{
                              mr: 1,
                              color: "text.secondary",
                              alignSelf: "flex-start",
                              mt: 1,
                            }}
                          />
                        ),
                        endAdornment: isUltraPlusFieldLocked("metaWhaleReceivingAddress") && (
                          <AdminPanelSettings 
                            sx={{ 
                              color: "text.secondary",
                              ml: 1,
                              alignSelf: "flex-start",
                              mt: 1,
                            }} 
                            fontSize="small"
                          />
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="T&S Upliner Link"
                      value={formData.metaWhaleUplinerLink}
                      onChange={handleInputChange("metaWhaleUplinerLink")}
                      disabled={!isEditing || isUltraPlusFieldLocked("metaWhaleUplinerLink")}
                      placeholder="Enter your T&S upliner link"
                      error={isUltraPlusFieldRejected("metaWhaleUplinerLink")}
                      helperText={getUltraPlusFieldHelperText(
                        "metaWhaleUplinerLink",
                        "Your upliner's T&S link"
                      )}
                      InputProps={{
                        startAdornment: (
                          <LinkIcon sx={{ mr: 1, color: "text.secondary" }} />
                        ),
                        endAdornment: isUltraPlusFieldLocked("metaWhaleUplinerLink") && (
                          <AdminPanelSettings 
                            sx={{ 
                              color: "text.secondary",
                              ml: 1 
                            }} 
                            fontSize="small"
                          />
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="T&S Upliner Received Address"
                      value={formData.metaWhaleUplinerReceivedAddress}
                      onChange={handleInputChange(
                        "metaWhaleUplinerReceivedAddress"
                      )}
                      disabled={!isEditing || isUltraPlusFieldLocked("metaWhaleUplinerReceivedAddress")}
                      placeholder="Enter your upliner's receiving address"
                      error={isUltraPlusFieldRejected("metaWhaleUplinerReceivedAddress")}
                      helperText={getUltraPlusFieldHelperText(
                        "metaWhaleUplinerReceivedAddress",
                        "Address where your upliner receives payments"
                      )}
                      multiline
                      rows={2}
                      InputProps={{
                        startAdornment: (
                          <AccountBalance
                            sx={{
                              mr: 1,
                              color: "text.secondary",
                              alignSelf: "flex-start",
                              mt: 1,
                            }}
                          />
                        ),
                        endAdornment: isUltraPlusFieldLocked("metaWhaleUplinerReceivedAddress") && (
                          <AdminPanelSettings 
                            sx={{ 
                              color: "text.secondary",
                              ml: 1,
                              alignSelf: "flex-start",
                              mt: 1,
                            }} 
                            fontSize="small"
                          />
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="V-Boost Referral Link"
                      value={formData.vBoostReferralLink}
                      onChange={handleInputChange("vBoostReferralLink")}
                      disabled={!isEditing || isUltraPlusFieldLocked("vBoostReferralLink")}
                      placeholder="Enter your V-Boost referral link"
                      error={
                        isUltraPlusFieldRejected("vBoostReferralLink") ||
                        Boolean(uniqueFieldConflicts.vBoostReferralLink)
                      }
                      helperText={
                        uniqueFieldConflicts.vBoostReferralLink ||
                        getUltraPlusFieldHelperText(
                          "vBoostReferralLink",
                          "Your V-Boost referral link for sharing"
                        )
                      }
                      InputProps={{
                        startAdornment: (
                          <LinkIcon sx={{ mr: 1, color: "text.secondary" }} />
                        ),
                        endAdornment: isUltraPlusFieldLocked("vBoostReferralLink") && (
                          <AdminPanelSettings 
                            sx={{ 
                              color: "text.secondary",
                              ml: 1 
                            }} 
                            fontSize="small"
                          />
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="V-Boost Receiving Address"
                      value={formData.vBoostReceivingAddress}
                      onChange={handleInputChange("vBoostReceivingAddress")}
                      disabled={!isEditing || isUltraPlusFieldLocked("vBoostReceivingAddress")}
                      placeholder="Enter your V-Boost receiving address"
                      error={
                        isUltraPlusFieldRejected("vBoostReceivingAddress") ||
                        Boolean(uniqueFieldConflicts.vBoostReceivingAddress)
                      }
                      helperText={
                        uniqueFieldConflicts.vBoostReceivingAddress ||
                        getUltraPlusFieldHelperText(
                          "vBoostReceivingAddress",
                          "Address where you receive V-Boost payments"
                        )
                      }
                      multiline
                      rows={2}
                      InputProps={{
                        startAdornment: (
                          <AccountBalance
                            sx={{
                              mr: 1,
                              color: "text.secondary",
                              alignSelf: "flex-start",
                              mt: 1,
                            }}
                          />
                        ),
                        endAdornment: isUltraPlusFieldLocked("vBoostReceivingAddress") && (
                          <AdminPanelSettings 
                            sx={{ 
                              color: "text.secondary",
                              ml: 1,
                              alignSelf: "flex-start",
                              mt: 1,
                            }} 
                            fontSize="small"
                          />
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="V-Boost Upliner Link"
                      value={formData.vBoostUplinerLink}
                      onChange={handleInputChange("vBoostUplinerLink")}
                      disabled={!isEditing || isUltraPlusFieldLocked("vBoostUplinerLink")}
                      placeholder="Enter your V-Boost upliner link"
                      error={isUltraPlusFieldRejected("vBoostUplinerLink")}
                      helperText={getUltraPlusFieldHelperText(
                        "vBoostUplinerLink",
                        "Your upliner's V-Boost link"
                      )}
                      InputProps={{
                        startAdornment: (
                          <LinkIcon sx={{ mr: 1, color: "text.secondary" }} />
                        ),
                        endAdornment: isUltraPlusFieldLocked("vBoostUplinerLink") && (
                          <AdminPanelSettings 
                            sx={{ 
                              color: "text.secondary",
                              ml: 1 
                            }} 
                            fontSize="small"
                          />
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="V-Boost Upliner Received Address"
                      value={formData.vBoostUplinerReceivedAddress}
                      onChange={handleInputChange(
                        "vBoostUplinerReceivedAddress"
                      )}
                      disabled={!isEditing || isUltraPlusFieldLocked("vBoostUplinerReceivedAddress")}
                      placeholder="Enter your upliner's receiving address"
                      error={isUltraPlusFieldRejected("vBoostUplinerReceivedAddress")}
                      helperText={getUltraPlusFieldHelperText(
                        "vBoostUplinerReceivedAddress",
                        "Address where your upliner receives V-Boost payments"
                      )}
                      multiline
                      rows={2}
                      InputProps={{
                        startAdornment: (
                          <AccountBalance
                            sx={{
                              mr: 1,
                              color: "text.secondary",
                              alignSelf: "flex-start",
                              mt: 1,
                            }}
                          />
                        ),
                        endAdornment: isUltraPlusFieldLocked("vBoostUplinerReceivedAddress") && (
                          <AdminPanelSettings 
                            sx={{ 
                              color: "text.secondary",
                              ml: 1,
                              alignSelf: "flex-start",
                              mt: 1,
                            }} 
                            fontSize="small"
                          />
                        ),
                      }}
                    />
                  </Grid>

                  {isEditing &&
                    (ultraPlusStatus === "none" ||
                      ultraPlusStatus === "rejected" ||
                      ultraPlusStatus === "partially_rejected") && (
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<VerifiedUser />}
                        onClick={handleSubmitUltraPlusApproval}
                        disabled={
                          submittingApproval ||
                          checkingUniqueFields ||
                          hasUniqueFieldConflicts ||
                          !hasAllUltraPlusFields(formData)
                        }
                        fullWidth
                        sx={{ py: 1.25 }}
                      >
                        {submittingApproval
                          ? "Submitting..."
                          : ultraPlusStatus === "partially_rejected" ||
                              ultraPlusStatus === "rejected"
                            ? "Resubmit for Approval"
                            : "Submit for Approval"}
                      </Button>
                      {!hasAllUltraPlusFields(formData) && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 1 }}
                        >
                          Fill all T&S and V-Boost fields before submitting.
                        </Typography>
                      )}
                    </Grid>
                  )}
                  </>
                  )}
                </Grid>
              </Paper>
            </Grid>

            {/* Address Management Section */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  ...glassStyles,
                  p: 3,
                  bgcolor: "rgba(255, 255, 255, 0.8)",
                }}
              >
                <AddressManagement user={user} primaryBlue={primaryBlue} />
              </Paper>
            </Grid>

            {/* Who Referred Me Section */}
            {renderMyReferrerSection()}
          </Grid>
        </Container>

        {/* Referral Tree Dialog */}
        <Dialog
          open={treeDialogOpen}
          onClose={() => setTreeDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              ...glassStyles,
              bgcolor: "rgba(255, 255, 255, 0.95)",
              maxHeight: "90vh",
            },
          }}
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <AccountTree sx={{ mr: 2, color: primaryBlue[700] }} />
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ color: primaryBlue[800], fontWeight: 700 }}
                  >
                    My Referral Network Tree
                  </Typography>
                  <Typography variant="body2" sx={{ color: primaryBlue[600] }}>
                    {userProfile?.name} ({user.email})
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton 
                  onClick={handleRefreshTree}
                  disabled={treeLoading}
                  title="Refresh tree"
                  sx={{ 
                    color: primaryBlue[600],
                    '&:hover': { bgcolor: primaryBlue[50] }
                  }}
                >
                  <Refresh sx={{ 
                    animation: treeLoading ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }} />
                </IconButton>
                <IconButton onClick={() => setTreeDialogOpen(false)}>
                  <Close />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            {treeLoading ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <CircularProgress sx={{ color: primaryBlue[600] }} />
                <Typography
                  variant="body1"
                  sx={{ mt: 2, color: primaryBlue[700] }}
                >
                  Loading referral tree...
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ mt: 1, color: primaryBlue[500], display: 'block' }}
                >
                  Optimized batch loading...
                </Typography>
              </Box>
            ) : referralTree ? (
              <>
                {referralTree.length === 0 ? (
                  <Alert
                    severity="info"
                    sx={{ ...glassStyles, bgcolor: "rgba(0, 114, 206, 0.1)" }}
                  >
                    <Typography variant="body1">
                      <strong>You</strong> haven't referred anyone yet.
                    </Typography>
                  </Alert>
                ) : (
                  <>
                    <Alert
                      severity="success"
                      sx={{
                        ...glassStyles,
                        bgcolor: "rgba(76, 175, 80, 0.1)",
                        mb: 3,
                      }}
                    >
                      <Typography variant="body1">
                        <strong>You</strong> have referred{" "}
                        <strong>{referralTree.length}</strong> people directly,
                        with{" "}
                        <strong>
                          {referralTree.reduce(
                            (sum, ref) => sum + ref.totalSubReferrals,
                            0
                          )}
                        </strong>{" "}
                        total sub-referrals in your network.
                      </Typography>
                    </Alert>

                    <Box sx={{ maxHeight: "60vh", overflowY: "auto", pr: 1 }}>
                      {referralTree.map((referral) => (
                        <ReferralTreeNode
                          key={`root-${referral.referredUserId}`}
                          referral={referral}
                          level={0}
                          parentId="root"
                        />
                      ))}
                    </Box>
                  </>
                )}
              </>
            ) : null}
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={() => setTreeDialogOpen(false)}
              variant="contained"
              sx={{
                bgcolor: primaryBlue[500],
                "&:hover": { bgcolor: primaryBlue[600] },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Referral Count Dialog */}
        <Dialog
          open={countDialogOpen}
          onClose={() => setCountDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              ...glassStyles,
              bgcolor: "rgba(255, 255, 255, 0.95)",
              maxHeight: "80vh",
            },
          }}
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Assessment sx={{ mr: 2, color: primaryBlue[700] }} />
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ color: primaryBlue[800], fontWeight: 700 }}
                  >
                    Referral Count by Level
                  </Typography>
                  <Typography variant="body2" sx={{ color: primaryBlue[600] }}>
                    Total users referred at each level
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={() => setCountDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            {countLoading ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <CircularProgress sx={{ color: primaryBlue[600] }} />
                <Typography
                  variant="body1"
                  sx={{ mt: 2, color: primaryBlue[700] }}
                >
                  Calculating referral counts...
                </Typography>
              </Box>
            ) : (
              <>
                {Object.keys(referralCounts).length === 0 ? (
                  <Alert
                    severity="info"
                    sx={{ ...glassStyles, bgcolor: "rgba(0, 114, 206, 0.1)" }}
                  >
                    <Typography variant="body1">
                      <strong>No referrals found.</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      You haven't referred anyone yet. Share your referral link to start building your network!
                    </Typography>
                  </Alert>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Alert
                      severity="success"
                      sx={{
                        ...glassStyles,
                        bgcolor: "rgba(76, 175, 80, 0.1)",
                        mb: 2,
                      }}
                    >
                      <Typography variant="body1">
                        <strong>Total Network Size:</strong>{" "}
                        <strong>
                          {Object.values(referralCounts).reduce((sum, count) => sum + count.total, 0)}
                        </strong> users across{" "}
                        <strong>{Object.keys(referralCounts).length}</strong> level{Object.keys(referralCounts).length === 1 ? '' : 's'}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Ultra Customers:</strong>{" "}
                        <strong>
                          {Object.values(referralCounts).reduce((sum, count) => sum + count.ultra, 0)}
                        </strong> ({Math.round((Object.values(referralCounts).reduce((sum, count) => sum + count.ultra, 0) / Object.values(referralCounts).reduce((sum, count) => sum + count.total, 0)) * 100) || 0}% of total)
                      </Typography>
                    </Alert>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={showUltraOnly}
                          onChange={(e) => setShowUltraOnly(e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: primaryBlue[500],
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: primaryBlue[300],
                            },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ fontWeight: 600, color: primaryBlue[700] }}>
                          Show Ultra Customers Only
                        </Typography>
                      }
                      sx={{ mb: 2 }}
                    />

                    {Object.entries(referralCounts)
                      .sort(([a], [b]) => parseInt(a) - parseInt(b))
                      .filter(([level, count]) => !showUltraOnly || count.ultra > 0)
                      .map(([level, count]) => {
                        const displayCount = showUltraOnly ? count.ultra : count.total;
                        const ultraPercentage = count.total > 0 ? Math.round((count.ultra / count.total) * 100) : 0;
                        
                        return (
                          <Card
                            key={level}
                            sx={{
                              ...cardGlassStyles,
                              bgcolor: showUltraOnly 
                                ? `rgba(255, 193, 7, ${0.05 + (parseInt(level) - 1) * 0.03})`
                                : `rgba(0, 114, 206, ${0.05 + (parseInt(level) - 1) * 0.03})`,
                              border: showUltraOnly
                                ? `2px solid rgba(255, 193, 7, ${0.2 + (parseInt(level) - 1) * 0.1})`
                                : `2px solid rgba(0, 114, 206, ${0.2 + (parseInt(level) - 1) * 0.1})`,
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                  <Chip
                                    label={`Level ${level}`}
                                    sx={{
                                      bgcolor: showUltraOnly
                                        ? `rgba(255, 193, 7, ${0.15 + parseInt(level) * 0.05})`
                                        : primaryBlue[100 + parseInt(level) * 50],
                                      color: showUltraOnly ? '#e65100' : primaryBlue[800],
                                      fontWeight: 700,
                                      fontSize: "0.9rem",
                                      mr: 2,
                                    }}
                                  />
                                  <Box>
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        color: showUltraOnly ? '#e65100' : primaryBlue[700],
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center'
                                      }}
                                    >
                                      {showUltraOnly && <Star sx={{ fontSize: 18, mr: 0.5 }} />}
                                      {displayCount} {showUltraOnly ? 'Ultra' : 'user'}{displayCount === 1 ? '' : 's'}
                                    </Typography>
                                    {!showUltraOnly && count.ultra > 0 && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: '#D4AF37',
                                          fontWeight: 600,
                                          display: 'flex',
                                          alignItems: 'center'
                                        }}
                                      >
                                        <Star sx={{ fontSize: 12, mr: 0.5 }} />
                                        {count.ultra} Ultra ({ultraPercentage}%)
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                                <People 
                                  sx={{ 
                                    color: showUltraOnly ? '#D4AF37' : primaryBlue[500],
                                    fontSize: 30
                                  }} 
                                />
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 1,
                                  color: showUltraOnly ? '#e65100' : primaryBlue[600],
                                }}
                              >
                                {showUltraOnly ? (
                                  level === "1" 
                                    ? "Direct Ultra customers - Ultra customers you referred directly"
                                    : `Level ${level} Ultra customers - Ultra customers referred by your level ${parseInt(level) - 1} referrals`
                                ) : (
                                  level === "1" 
                                    ? "Direct referrals - people you referred directly"
                                    : `Level ${level} referrals - people referred by your level ${parseInt(level) - 1} referrals`
                                )}
                              </Typography>
                            </CardContent>
                          </Card>
                        );
                      })
                    }
                  </Box>
                )}
              </>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={() => {
                setCountDialogOpen(false);
                setShowUltraOnly(false); // Reset filter when closing
              }}
              variant="contained"
              sx={{
                bgcolor: primaryBlue[500],
                "&:hover": { bgcolor: primaryBlue[600] },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default ProfileSettings;
